import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  X, 
  Eye, 
  DollarSign,
  Calendar,
  FileText,
  Search
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface Transaction {
  id: number;
  transaction_date: string;
  description: string;
  amount: number;
  merchant: string;
  account_type: string;
  account_last_four: string;
  matched: boolean;
  receipt_count: number;
  matched_amount: number;
  remaining_amount: number;
}

interface Receipt {
  id: number;
  total_amount: number;
  transaction_date: string;
  vendor_name: string;
  filename: string;
  document_id: string;
  payment_method: string;
  check_number: string;
}

interface Match {
  id: number;
  receipt_id: number;
  match_confidence: number;
  match_type: string;
  matched_at: string;
  notes: string;
  receipt_amount: number;
  receipt: Receipt;
}

interface Suggestion {
  id: number;
  total_amount: number;
  transaction_date: string;
  vendor_name: string;
  filename: string;
  document_id: string;
  match_score: number;
}

export default function EnhancedReconciliation() {
  const queryClient = useQueryClient();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unmatched' | 'partial' | 'matched'>('all');

  // Get transactions needing assistance
  const { data: needsAssistanceData, isLoading } = useQuery({
    queryKey: ['needs-assistance', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      
      const response = await axios.get(`${API_URL}/reconciliation/needs-assistance?${params}`);
      return response.data;
    },
    enabled: !!dateFrom && !!dateTo,
  });

  // Get transaction details when one is selected
  const { data: transactionDetails } = useQuery({
    queryKey: ['transaction-details', selectedTransaction?.id],
    queryFn: async () => {
      if (!selectedTransaction) return null;
      const response = await axios.get(`${API_URL}/reconciliation/transactions/${selectedTransaction.id}/details`);
      return response.data;
    },
    enabled: !!selectedTransaction,
  });

  // Get AI suggestions for selected transaction
  const { data: suggestionsData } = useQuery({
    queryKey: ['ai-suggestions', selectedTransaction?.id],
    queryFn: async () => {
      if (!selectedTransaction) return null;
      const response = await axios.get(`${API_URL}/reconciliation/transactions/${selectedTransaction.id}/suggestions`);
      return response.data;
    },
    enabled: !!selectedTransaction && showSuggestions,
  });

  // Add receipt to transaction mutation
  const addReceiptMutation = useMutation({
    mutationFn: async ({ transactionId, receiptId, notes, receiptAmount }: {
      transactionId: number;
      receiptId: number;
      notes?: string;
      receiptAmount?: number;
    }) => {
      const response = await axios.post(`${API_URL}/reconciliation/transactions/${transactionId}/add-receipt`, {
        receiptId,
        notes,
        receiptAmount
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Receipt added to transaction successfully');
      queryClient.invalidateQueries({ queryKey: ['transaction-details'] });
      queryClient.invalidateQueries({ queryKey: ['needs-assistance'] });
      setShowSuggestions(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add receipt');
    },
  });

  // Remove receipt from transaction mutation
  const removeReceiptMutation = useMutation({
    mutationFn: async ({ transactionId, receiptId }: {
      transactionId: number;
      receiptId: number;
    }) => {
      const response = await axios.delete(`${API_URL}/reconciliation/transactions/${transactionId}/receipts/${receiptId}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Receipt removed from transaction');
      queryClient.invalidateQueries({ queryKey: ['transaction-details'] });
      queryClient.invalidateQueries({ queryKey: ['needs-assistance'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove receipt');
    },
  });

  const transactions = needsAssistanceData?.transactions || [];
  const currentMatches: Match[] = transactionDetails?.currentMatches || [];
  const suggestedReceipts: Suggestion[] = suggestionsData?.suggestions || [];

  const filteredTransactions = transactions.filter((txn: Transaction) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'unmatched') return txn.receipt_count === 0;
    if (filterStatus === 'partial') return txn.receipt_count > 0 && txn.remaining_amount > 0.01;
    if (filterStatus === 'matched') return txn.receipt_count > 0 && txn.remaining_amount <= 0.01;
    return true;
  });

  const handleAddReceipt = (receiptId: number, receiptAmount?: number) => {
    if (!selectedTransaction) return;
    
    addReceiptMutation.mutate({
      transactionId: selectedTransaction.id,
      receiptId,
      receiptAmount
    });
  };

  const handleRemoveReceipt = (receiptId: number) => {
    if (!selectedTransaction) return;
    
    removeReceiptMutation.mutate({
      transactionId: selectedTransaction.id,
      receiptId
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (txn: Transaction) => {
    if (txn.receipt_count === 0) return 'text-red-600 bg-red-50';
    if (txn.remaining_amount <= 0.01) return 'text-green-600 bg-green-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const getStatusText = (txn: Transaction) => {
    if (txn.receipt_count === 0) return 'Needs Assistance';
    if (txn.remaining_amount <= 0.01) return 'Fully Matched';
    return 'Partially Matched';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Enhanced Reconciliation</h1>
        <p className="text-gray-600">
          Match receipts with bank transactions and manage multiple receipts per transaction
        </p>
      </div>

      {/* Date Range and Filters */}
      <div className="card">
        <div className="flex items-center gap-4 mb-4">
          <div>
            <label className="label">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">Status Filter</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="input"
            >
              <option value="all">All Transactions</option>
              <option value="unmatched">Needs Assistance</option>
              <option value="partial">Partially Matched</option>
              <option value="matched">Fully Matched</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transactions List */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Transactions ({filteredTransactions.length})</h2>
            <div className="text-sm text-gray-500">
              {filteredTransactions.filter((t: Transaction) => t.receipt_count === 0).length} need assistance
            </div>
          </div>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredTransactions.map((txn: Transaction) => (
              <div
                key={txn.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedTransaction?.id === txn.id 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedTransaction(txn)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{txn.merchant || txn.description}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(txn)}`}>
                        {getStatusText(txn)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(Math.abs(txn.amount))}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(txn.transaction_date)}
                        </span>
                      </div>
                      {txn.receipt_count > 0 && (
                        <div className="text-xs">
                          {txn.receipt_count} receipt{txn.receipt_count !== 1 ? 's' : ''} attached
                          {txn.remaining_amount > 0.01 && (
                            <span className="text-yellow-600 ml-2">
                              ({formatCurrency(txn.remaining_amount)} remaining)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {txn.receipt_count > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTransaction(txn);
                          setShowSuggestions(true);
                        }}
                        className="p-1 text-primary-600 hover:text-primary-700"
                        title="Add another receipt"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    )}
                    <Eye className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transaction Details */}
        <div className="card">
          {selectedTransaction ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Transaction Details</h2>
                <button
                  onClick={() => setShowSuggestions(!showSuggestions)}
                  className="btn-secondary text-sm"
                >
                  <Search className="h-4 w-4 mr-1" />
                  AI Suggestions
                </button>
              </div>

              {/* Transaction Info */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Merchant:</span>
                    <div>{selectedTransaction.merchant || selectedTransaction.description}</div>
                  </div>
                  <div>
                    <span className="font-medium">Amount:</span>
                    <div>{formatCurrency(Math.abs(selectedTransaction.amount))}</div>
                  </div>
                  <div>
                    <span className="font-medium">Date:</span>
                    <div>{formatDate(selectedTransaction.transaction_date)}</div>
                  </div>
                  <div>
                    <span className="font-medium">Account:</span>
                    <div>{selectedTransaction.account_type} ****{selectedTransaction.account_last_four}</div>
                  </div>
                </div>
                
                {selectedTransaction.remaining_amount > 0.01 && (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <div className="text-sm text-yellow-800">
                      <strong>Remaining Amount:</strong> {formatCurrency(selectedTransaction.remaining_amount)}
                    </div>
                  </div>
                )}
              </div>

              {/* Current Matches */}
              <div className="mb-4">
                <h3 className="font-medium mb-2">Attached Receipts ({currentMatches.length})</h3>
                <div className="space-y-2">
                  {currentMatches.map((match) => (
                    <div key={match.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{match.receipt.filename}</span>
                          <span className="text-sm text-gray-500">
                            ({formatCurrency(match.receipt_amount || match.receipt.total_amount)})
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {match.receipt.vendor_name} • {formatDate(match.receipt.transaction_date)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/documents/${match.receipt.document_id}`}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleRemoveReceipt(match.receipt_id)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Suggestions */}
              {showSuggestions && (
                <div>
                  <h3 className="font-medium mb-2">AI Suggested Matches</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {suggestedReceipts.map((suggestion) => (
                      <div key={suggestion.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="font-medium">{suggestion.filename}</span>
                            <span className="text-sm text-gray-500">
                              ({formatCurrency(suggestion.total_amount)})
                            </span>
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                              {Math.round(suggestion.match_score * 100)}% match
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {suggestion.vendor_name} • {formatDate(suggestion.transaction_date)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/documents/${suggestion.document_id}`}
                            className="p-1 text-gray-500 hover:text-gray-700"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => handleAddReceipt(suggestion.id)}
                            className="p-1 text-green-500 hover:text-green-700"
                            disabled={addReceiptMutation.isPending}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Select a transaction to view details and manage receipts
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
